import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { Input } from '../components/ui/input'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { MailWarning } from 'lucide-react'

const SignInWithEmail: React.FC = () => {
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<React.ReactNode>('')
  const navigate = useNavigate()
  const { requestMagicLink, athlete } = useAuth()

  // If already signed in, redirect to home
  useEffect(() => {
    if (athlete) {
      navigate('/', { replace: true })
    }
  }, [athlete, navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      await requestMagicLink(email)
      setSuccess(true)
    } catch (err: any) {
      const msg: string = err.message || ''
      if (msg === 'User not found') {
        setError(
          <span>
            No account found for that email.{' '}
            <Link
              to="/signup"
              className="text-[#4d7c0f] hover:text-[#a8e45a] font-medium underline"
            >
              Sign up instead?
            </Link>
          </span>,
        )
      } else {
        setError(msg || 'Failed to send magic link. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  if (success) {
    return (
      <div className="flex items-center justify-center min-h-screen relative">
        <div className="fixed inset-0 -z-10 bg-background"></div>

        <div className="w-full max-w-md px-4">
          <Card variant="dark">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <img
                  src="https://adthlete.us-iad-1.linodeobjects.com/public/adthlete_logomark.png"
                  alt="Athios Logo"
                  className="w-10 h-10"
                />
              </div>
              <CardTitle className="text-2xl font-bold text-foreground">
                Check Your Email
              </CardTitle>
              <CardDescription className="mt-2 text-gray-600">
                We've sent you a magic link to sign in.
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              <p className="text-gray-700">
                Click the link in the email to sign in securely. The link expires in 15 minutes.
              </p>
              <div className="flex items-start gap-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg px-4 py-3 text-left">
                <MailWarning className="h-5 w-5 text-yellow-600 shrink-0 mt-0.5" />
                <p className="text-yellow-700 text-sm font-medium">
                  Don't see it? <strong>Check your Spam or Junk folder</strong> — these emails
                  sometimes get filtered.
                </p>
              </div>
              <Link
                to="/signin"
                className="block text-[#4d7c0f] hover:text-[#a8e45a] font-medium text-sm"
              >
                Back to Sign In
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-center justify-center min-h-screen relative">
      <div className="fixed inset-0 -z-10 bg-background"></div>

      <div className="w-full max-w-md px-4">
        <Card variant="dark">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <img
                src="https://adthlete.us-iad-1.linodeobjects.com/public/adthlete_logomark.png"
                alt="Athios Logo"
                className="w-10 h-10"
              />
            </div>
            <CardTitle className="text-2xl font-bold text-foreground">
              Sign In With Email
            </CardTitle>
            <CardDescription className="mt-2 text-gray-600">
              Enter your email to receive a secure sign-in link.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error && <p className="text-red-500 mb-4 text-sm text-center">{error}</p>}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-card text-foreground border-foreground/10 focus:border-[#C0FD72] focus:ring-[#C0FD72]/20"
                  required
                />
              </div>
              <Button
                type="submit"
                variant="lime"
                className="w-full font-medium"
                disabled={isLoading}
              >
                {isLoading ? 'Sending...' : 'Send Magic Link'}
              </Button>
            </form>

            <div className="mt-4 text-center">
              <Link
                to="/signin"
                className="text-[#4d7c0f] hover:text-[#a8e45a] font-medium text-sm"
              >
                Back to Sign In
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default SignInWithEmail
